/**
 * Grouping for the alarm list.
 *
 * `GET /alarms?group_by=…` answers with counted groups and a sample of each
 * (`top_alarms`); that is the authority, because its counts cover the whole
 * result and not just the pages the browser happens to hold. When the toggle is
 * flipped without a round trip, the console groups what it has instead. Both
 * end up as `AlarmGroupView`, so the page renders one shape.
 */
import { SEVERITY_RANK, type Severity } from '@vam/schema';
import type { Alarm, AlarmGroup } from '../../lib/schemas.js';
import { humanise } from '../../lib/format.js';
import type { GroupBy } from './filters.js';

export interface AlarmGroupView {
  key: string;
  label: string;
  /** Alarms in the whole result, which may exceed `items.length`. */
  count: number;
  items: Alarm[];
}

/** Resolves the ids on an alarm to the names people recognise. */
export interface GroupLabels {
  integration?: (id: string) => string | undefined;
  team?: (id: string) => string | undefined;
}

export function groupKeyOf(
  alarm: Alarm,
  groupBy: GroupBy,
  labels: GroupLabels = {},
): { key: string; label: string } {
  switch (groupBy) {
    case 'severity':
      return { key: alarm.severity, label: humanise(alarm.severity) };
    case 'integration':
      return {
        key: alarm.integration_id,
        label: labels.integration?.(alarm.integration_id) ?? alarm.integration_id.slice(0, 8),
      };
    case 'category':
      return { key: alarm.category ?? '—', label: alarm.category ?? 'No category' };
    case 'entity':
      return { key: alarm.entity.name || '—', label: alarm.entity.name || 'No entity' };
    case 'team':
      return {
        key: alarm.team_id,
        label: labels.team?.(alarm.team_id) ?? alarm.team_id.slice(0, 8),
      };
    default:
      return { key: '', label: '' };
  }
}

/** Order groups so the ones that matter are on top. */
function sortGroups(groups: AlarmGroupView[], groupBy: GroupBy): AlarmGroupView[] {
  if (groupBy === 'severity') {
    return [...groups].sort(
      (a, b) => (SEVERITY_RANK[b.key as Severity] ?? 0) - (SEVERITY_RANK[a.key as Severity] ?? 0),
    );
  }
  return [...groups].sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));
}

/** The API's grouped answer, in the shape the list renders. */
export function viewFromServerGroups(
  groups: AlarmGroup[],
  groupBy: GroupBy,
  labels: GroupLabels = {},
): AlarmGroupView[] {
  const views = groups.map((group) => {
    const sample = group.top_alarms.length > 0 ? group.top_alarms : [];
    const first = sample[0] ?? group.latest_alarm;
    const key = group.key ?? (first ? groupKeyOf(first, groupBy, labels).key : '—');
    const label = group.label ?? (first ? groupKeyOf(first, groupBy, labels).label : 'Not set');
    return {
      key,
      // Ids read as noise in a group header; resolve them the same way the rows do.
      label: resolveLabel(groupBy, key, label, labels),
      count: group.count,
      items: sample.length > 0 ? sample : group.latest_alarm ? [group.latest_alarm] : [],
    };
  });
  return sortGroups(views, groupBy);
}

function resolveLabel(
  groupBy: GroupBy,
  key: string,
  fallback: string,
  labels: GroupLabels,
): string {
  if (groupBy === 'integration') return labels.integration?.(key) ?? fallback;
  if (groupBy === 'team') return labels.team?.(key) ?? fallback;
  if (groupBy === 'severity') return humanise(fallback);
  return fallback;
}

export function groupAlarms(
  alarms: Alarm[],
  groupBy: GroupBy,
  labels: GroupLabels = {},
): AlarmGroupView[] {
  if (!groupBy) return [];
  const map = new Map<string, AlarmGroupView>();
  for (const alarm of alarms) {
    const { key, label } = groupKeyOf(alarm, groupBy, labels);
    const existing = map.get(key);
    if (existing) {
      existing.items.push(alarm);
      existing.count += 1;
    } else {
      map.set(key, { key, label, count: 1, items: [alarm] });
    }
  }
  return sortGroups([...map.values()], groupBy);
}
